import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DangerZone from "@/components/DangerZone";
import NotificationWatchlist from "@/components/NotificationWatchlist";
import { ShieldCheck, UserPlus, X, Copy, Check, Link2, Trash2, Clock, RefreshCw } from "lucide-react";

const ROLE_LABEL = {
  platform_admin: "Platform admin",
  owner: "Owner",
  administrative: "Administrative",
  call_center: "Call center / CSR",
  technician: "Technician",
};

// Roles selectable in the Create User modal (platform_admin excluded)
const ASSIGNABLE_ROLES = [
  { id: "owner",          label: "Owner" },
  { id: "administrative", label: "Administrative" },
  { id: "call_center",    label: "Call center / CSR" },
  { id: "technician",     label: "Technician" },
];

const STATUS_STYLES = {
  pending:  "bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]",
  consumed: "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]",
  expired:  "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]",
  revoked:  "bg-[#fee2e2] text-[#991b1b] border-[#f87171]",
};

function fmtRel(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.round((then - now) / (1000 * 60 * 60 * 24));
  if (diff > 1) return `in ${diff} days`;
  if (diff === 1) return "in 1 day";
  if (diff === 0) return "today";
  if (diff === -1) return "1 day ago";
  return `${-diff} days ago`;
}

function InviteGeneratorModal({ user, tenant, onClose, onCreated }) {
  const isAdmin = user?.role === "platform_admin";
  // Owners cannot invite an owner; platform_admin can invite any role incl. owner
  const roles = isAdmin
    ? [{ id: "owner", label: "Owner" }, ...ASSIGNABLE_ROLES.filter((r) => r.id !== "owner")]
    : ASSIGNABLE_ROLES.filter((r) => r.id !== "owner");
  const [form, setForm] = useState({ role: "technician", mode: "locked", email: "", expires_in_days: 7 });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const payload = {
        role: form.role,
        expires_in_days: Number(form.expires_in_days),
      };
      if (form.mode === "locked") {
        if (!form.email.trim()) throw new Error("Email is required for locked invites");
        payload.email = form.email.trim();
        payload.single_use = true;
      } else {
        payload.single_use = false;
      }
      const { data } = await api.post("/invites", payload);
      const url = `${window.location.origin}/${data.tenant_slug || tenant?.slug}/invite/${data.token}`;
      setCreated({ ...data, url });
      onCreated?.();
    } catch (e) {
      const d = e.response?.data?.detail || e.message;
      setErr(typeof d === "string" ? d : "Could not create invite");
    } finally { setSaving(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(created.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="invite-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[560px] my-4 shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-[#eef1f6] flex items-center justify-between">
          <div className="font-display font-bold text-[17px] text-[#0e1420]">
            {created ? "Invite link ready" : "Generate invite link"}
          </div>
          <button onClick={onClose} data-testid="invite-modal-close" className="p-1.5 rounded-md hover:bg-[#f5f6f8]"><X size={16} /></button>
        </div>

        {!created ? (
          <form onSubmit={submit} className="p-5 grid gap-4">
            <div>
              <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                      data-testid="invite-role"
                      className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[14px] outline-none focus:border-[#0072bc] bg-white">
                {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Link type</label>
              <div className="inline-flex bg-[#f5f6f8] rounded-lg p-1 border border-[#e7e9ee]" data-testid="invite-mode">
                {[
                  { id: "locked", label: "Locked to email" },
                  { id: "open",   label: "Open reusable" },
                ].map((o) => (
                  <button
                    key={o.id} type="button"
                    onClick={() => setForm({ ...form, mode: o.id })}
                    data-testid={`invite-mode-${o.id}`}
                    className={`px-3 py-1.5 text-[12px] rounded-md font-semibold transition-colors ${
                      form.mode === o.id ? "bg-white text-[#0072bc] shadow-sm" : "text-[color:var(--cc-ink-muted)]"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-1.5">
                {form.mode === "locked"
                  ? "Only the address below can accept. Single-use."
                  : "Anyone with the link can create an account with this role until it expires. Reusable."}
              </div>
            </div>

            {form.mode === "locked" && (
              <div>
                <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Recipient email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="invite-email"
                       className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[14px] outline-none focus:border-[#0072bc]" />
              </div>
            )}

            <div>
              <label className="block text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Expires in</label>
              <select value={form.expires_in_days} onChange={(e) => setForm({ ...form, expires_in_days: e.target.value })}
                      data-testid="invite-expires"
                      className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[14px] outline-none focus:border-[#0072bc] bg-white">
                <option value={1}>1 day</option>
                <option value={3}>3 days</option>
                <option value={7}>7 days (default)</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>

            {err && <div className="text-[12.5px] text-[#991b1b] font-semibold" data-testid="invite-form-error">{err}</div>}
            <button type="submit" disabled={saving} data-testid="invite-submit"
                    className="cc-btn-primary w-full text-[13.5px] py-2.5 disabled:opacity-60">
              {saving ? "Creating…" : "Generate link"}
            </button>
          </form>
        ) : (
          <div className="p-5 space-y-4">
            <div className="text-[13px] text-[color:var(--cc-ink-muted)]">
              Share this link manually. It expires <span className="font-semibold text-[#0e1420]">{fmtRel(created.expires_at)}</span>.
              {created.email ? <> Locked to <span className="font-mono-data font-semibold text-[#0e1420]">{created.email}</span>.</> : <> Reusable by anyone with the link.</>}
            </div>
            <div className="flex items-stretch gap-2">
              <input readOnly value={created.url} data-testid="invite-generated-url"
                     className="flex-1 bg-[#fafbfc] border border-[#e7e9ee] rounded-lg px-3 py-2.5 text-[12.5px] font-mono-data text-[#0e1420] outline-none" />
              <button onClick={copyLink} data-testid="invite-copy-btn"
                      className="cc-btn-primary text-[13px] py-2 px-3 shrink-0">
                {copied ? <><Check size={14} strokeWidth={2} /> Copied</> : <><Copy size={14} strokeWidth={2} /> Copy</>}
              </button>
            </div>
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)]">
              Role: <span className="font-semibold text-[#0e1420]">{ROLE_LABEL[created.role]}</span>. This link will not be shown again — copy it now.
            </div>
            <button onClick={onClose} data-testid="invite-done-btn" className="cc-btn-secondary w-full text-[13px] py-2">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateUserModal({ user, tenant, onClose, onCreated }) {
  const isAdmin = user?.role === "platform_admin";
  const [tenants, setTenants] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", role: "administrative", tenant_id: tenant?.id || "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isAdmin) api.get("/tenants").then(({ data }) => setTenants(data)).catch(() => {});
  }, [isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email, role: form.role, tenant_id: form.tenant_id || tenant?.id };
      const { data } = await api.post("/users", payload);
      setCreated(data);
      if (onCreated) onCreated();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not create user");
    } finally { setSaving(false); }
  };

  const copyCreds = () => {
    navigator.clipboard.writeText(`${created.email} / ${created.generated_password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="create-user-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[520px] my-4 shadow-2xl overflow-hidden">
        <div className="p-5 border-b border-[#eef1f6] flex items-center justify-between">
          <div className="font-display font-bold text-[17px] text-[#0e1420]">
            {created ? "User created" : "Create a user"}
          </div>
          <button onClick={onClose} data-testid="create-user-close" className="p-1.5 rounded-md hover:bg-[#f5f6f8]"><X size={16} /></button>
        </div>

        {!created ? (
          <form onSubmit={submit} className="p-5 grid gap-3">
            <div>
              <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cu-name" className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[14px] outline-none focus:border-[#0072bc]" />
            </div>
            <div>
              <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cu-email" className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[14px] outline-none focus:border-[#0072bc]" />
            </div>
            <div>
              <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="cu-role" className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[14px] outline-none focus:border-[#0072bc] bg-white">
                {ASSIGNABLE_ROLES.filter((r) => r.id !== "owner" || user?.role !== "administrative").map((r) => (<option key={r.id} value={r.id}>{r.label}</option>))}
              </select>
              <div className="text-[12px] text-[color:var(--cc-ink-muted)] mt-1">Platform admin isn&apos;t assignable from this flow.</div>
            </div>
            {isAdmin && (
              <div>
                <label className="block text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-1.5">Tenant</label>
                <select required value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })} data-testid="cu-tenant" className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[14px] outline-none focus:border-[#0072bc] bg-white">
                  <option value="">Select tenant…</option>
                  {tenants.map((t) => (<option key={t.id} value={t.id}>{t.business_name} · /{t.slug}</option>))}
                </select>
              </div>
            )}
            {!isAdmin && (
              <div className="text-[13px] text-[color:var(--cc-ink-muted)] bg-[#f5f6f8] rounded-md px-3 py-2 border border-[#e7e9ee]">
                Creating for tenant: <span className="font-semibold text-[#0e1420]">{tenant?.business_name}</span>
              </div>
            )}
            {err && <div className="text-[13px] text-[#c73535] bg-[#fdecec] border border-[#f4c9c9] rounded-md px-3 py-2" data-testid="cu-error">{err}</div>}
            <button type="submit" disabled={saving} data-testid="cu-submit" className="cc-btn-primary text-[13px] mt-1 disabled:opacity-60">
              {saving ? "Creating…" : "Create user"}
            </button>
          </form>
        ) : (
          <div className="p-5 space-y-4">
            <div className="text-[14px] text-[#0e1420]">
              Login for <span className="font-semibold">{created.name}</span> is ready. Copy these credentials now — the password is not stored in plaintext and can&apos;t be shown again.
            </div>
            <div className="bg-[#f5f6f8] border border-[#e7e9ee] rounded-lg p-4 font-mono-data text-[13px] space-y-1" data-testid="cu-credentials">
              <div><span className="text-[color:var(--cc-ink-muted)]">Email: </span><span className="text-[#0e1420]">{created.email}</span></div>
              <div><span className="text-[color:var(--cc-ink-muted)]">Password: </span><span className="text-[#0e1420]">{created.generated_password}</span></div>
              <div><span className="text-[color:var(--cc-ink-muted)]">Role: </span><span className="text-[#0e1420]">{ROLE_LABEL[created.role] || created.role}</span></div>
            </div>
            <div className="flex gap-2">
              <button onClick={copyCreds} data-testid="cu-copy" className="cc-btn-primary text-[13px]">
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy email + password</>}
              </button>
              <button onClick={onClose} data-testid="cu-done" className="text-[13px] font-semibold px-4 py-2 rounded-md border border-[#e7e9ee] hover:bg-[#f5f6f8]">Done</button>
            </div>
            <div className="text-[12px] text-[color:var(--cc-ink-muted)]">Email delivery of credentials is deferred to Phase 2. For now, share these securely (password manager, encrypted channel).</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const { tenant } = useOutletContext();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  // User management state (2026-02 batch)
  const [showDeactivated, setShowDeactivated] = useState(true);
  const [savingUserId, setSavingUserId] = useState(null);
  const [pendingRole, setPendingRole] = useState(null);        // {user, newRole}
  const [pendingDeactivate, setPendingDeactivate] = useState(null);
  const [pendingHardDelete, setPendingHardDelete] = useState(null);
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState("");
  // CC-093 (2026-09-29) — admin password reset. pendingReset is the target
  // user, resetResult is the endpoint response (reset_url/expires_at), and
  // resetCopied flags whether the URL is already on the clipboard so we
  // can flip the button label without re-triggering a copy.
  const [pendingReset, setPendingReset] = useState(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [resetCopied, setResetCopied] = useState(false);
  const [resetErr, setResetErr] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const canCreate = user?.role === "platform_admin" || user?.role === "owner" || user?.role === "administrative";
  const loadUsers = useCallback(
    () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([])),
    []
  );
  const loadInvites = useCallback(() => {
    if (!canCreate) return;
    api.get("/invites").then(({ data }) => setInvites(data)).catch(() => setInvites([]));
  }, [canCreate]);
  useEffect(() => { loadUsers(); loadInvites(); }, [loadUsers, loadInvites]);

  // User mutation helpers — every path funnels through the /users/{id} PATCH
  // or DELETE endpoint, then reloads the list. Errors surface via alert() for
  // now (same UX pattern as revoke() / regenerate() below).
  const patchUser = async (u, patch) => {
    setSavingUserId(u.id);
    try {
      await api.patch(`/users/${u.id}`, patch);
      await loadUsers();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Update failed";
      window.alert(detail);
    } finally {
      setSavingUserId(null);
    }
  };
  const confirmRoleChange = async () => {
    if (!pendingRole) return;
    await patchUser(pendingRole.user, { role: pendingRole.newRole });
    setPendingRole(null);
  };
  const confirmDeactivate = async () => {
    if (!pendingDeactivate) return;
    await patchUser(pendingDeactivate, { active: false });
    setPendingDeactivate(null);
  };
  const reactivateUser = async (u) => { await patchUser(u, { active: true }); };

  // CC-093 (2026-09-29) — open the reset-password modal for a target user.
  // Fires POST /api/admin/users/{id}/mint-reset-token with an 8-hour TTL
  // (matches the operational default we set for the Nathanael Toms lockout),
  // auto-copies the returned URL to the clipboard, and shows the modal so
  // the admin can hand the URL to the user out-of-band. Errors surface
  // inline in the modal so the admin knows whether to retry or fall back
  // to Deactivate/Delete + fresh invite.
  const openResetModal = async (u) => {
    setPendingReset(u);
    setResetResult(null);
    setResetCopied(false);
    setResetErr("");
    setResetBusy(true);
    try {
      const { data } = await api.post(
        `/admin/users/${u.id}/mint-reset-token`,
        { expires_in_hours: 8 },
      );
      setResetResult(data);
      // Best-effort auto-copy. navigator.clipboard is only available in
      // secure contexts (HTTPS or localhost); on failure the admin can
      // still hit the Copy button manually.
      try {
        await navigator.clipboard.writeText(data.reset_url);
        setResetCopied(true);
      } catch {
        // silent — copy button below is the fallback
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message
        || "Failed to mint reset token";
      setResetErr(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setResetBusy(false);
    }
  };

  const copyResetUrl = async () => {
    if (!resetResult?.reset_url) return;
    try {
      await navigator.clipboard.writeText(resetResult.reset_url);
      setResetCopied(true);
    } catch {
      // fallback: select the input so the admin can Ctrl+C
      const el = document.querySelector('[data-testid="reset-password-url-input"]');
      if (el) { el.focus(); el.select(); }
    }
  };

  const closeResetModal = () => {
    setPendingReset(null);
    setResetResult(null);
    setResetCopied(false);
    setResetErr("");
  };
  const confirmHardDelete = async () => {
    if (!pendingHardDelete) return;
    if (hardDeleteConfirm.toLowerCase().trim() !== (pendingHardDelete.email || "").toLowerCase()) {
      window.alert("Type the exact email to confirm.");
      return;
    }
    setSavingUserId(pendingHardDelete.id);
    try {
      await api.delete(`/users/${pendingHardDelete.id}`, {
        params: { confirm_email: pendingHardDelete.email },
      });
      await loadUsers();
      setPendingHardDelete(null);
      setHardDeleteConfirm("");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Delete failed";
      window.alert(detail);
    } finally {
      setSavingUserId(null);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite? The link will stop working immediately.")) return;
    try { await api.delete(`/invites/${id}`); loadInvites(); } catch { /* toast handled visually */ }
  };
  const [regenerated, setRegenerated] = useState(null); // { token, url, email, role, expires_at }
  const [regenCopied, setRegenCopied] = useState(false);
  const regenerate = async (inv) => {
    const label = inv.email ? `for ${inv.email}` : "(open reusable)";
    if (!window.confirm(`Regenerate this invite ${label}? The current link will stop working and a fresh one will replace it.`)) return;
    try {
      const { data } = await api.post(`/invites/${inv.id}/regenerate`);
      const url = `${window.location.origin}/${data.tenant_slug || tenant?.slug}/invite/${data.token}`;
      setRegenerated({ ...data, url });
      setRegenCopied(false);
      loadInvites();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Regeneration failed");
    }
  };
  const copyRegenerated = () => {
    if (!regenerated) return;
    navigator.clipboard.writeText(regenerated.url);
    setRegenCopied(true);
    setTimeout(() => setRegenCopied(false), 2500);
  };

  return (
    <div data-testid="settings-page" className="space-y-5">
      <header>
        <div className="text-[12px] uppercase tracking-[.12em] text-[color:var(--cc-ink-muted)] font-semibold">Tenant configuration</div>
        <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1">Settings</h1>
        <p className="text-[14px] text-[color:var(--cc-ink-muted)] mt-1">Users, roles, and tenant-level configuration for {tenant?.business_name}.</p>
      </header>

      <section className="bg-white border border-[#e7e9ee] rounded-xl p-5">
        <h3 className="font-display font-semibold text-[15px] text-[#0e1420] mb-4">Tenant</h3>
        <dl className="grid sm:grid-cols-2 gap-4 text-[14px]">
          <div><dt className="text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">Business</dt><dd className="mt-1 font-semibold">{tenant?.business_name}</dd></div>
          <div><dt className="text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">Location</dt><dd className="mt-1">{tenant?.location}</dd></div>
          <div><dt className="text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">Plan</dt><dd className="mt-1"><span className="inline-block text-[11.5px] font-mono-data uppercase tracking-widest bg-[#e7f4fb] text-[#1c6a99] border border-[#c5e0ee] rounded px-2 py-0.5">{tenant?.plan_tier}</span></dd></div>
          <div><dt className="text-[11.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)]">Status</dt><dd className="mt-1"><span className="inline-block text-[11.5px] font-mono-data uppercase tracking-widest bg-[#e6f9f2] text-[#0e8a68] border border-[#c1eddc] rounded px-2 py-0.5">{tenant?.status}</span></dd></div>
        </dl>
      </section>

      <section className="bg-white border border-[#e7e9ee] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4 gap-3">
          <h3 className="font-display font-semibold text-[15px] text-[#0e1420]">Users &amp; roles</h3>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <div className="hidden sm:flex items-center gap-1.5 text-[12px] text-[color:var(--cc-blue)]"><ShieldCheck size={14} strokeWidth={1.8} /> Tenant-scoped</div>
            {canCreate && (
              <>
                <button onClick={() => setInviting(true)} data-testid="invite-link-btn"
                        className="text-[13px] font-semibold px-3 py-2 rounded-md border border-[#0072bc] text-[#0072bc] hover:bg-[#e7f4fb] inline-flex items-center gap-1.5">
                  <Link2 size={14} strokeWidth={1.8} /> Generate invite link
                </button>
                <button onClick={() => setCreating(true)} data-testid="create-user-btn"
                        className="cc-btn-primary text-[13px] py-2 px-3">
                  <UserPlus size={14} strokeWidth={1.8} /> Create user
                </button>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 mb-3">
          <label className="text-[12px] text-[color:var(--cc-ink-muted)] inline-flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={!showDeactivated}
              onChange={(e) => setShowDeactivated(!e.target.checked)}
              data-testid="hide-deactivated-toggle"
            />
            Show active only
          </label>
          <div className="text-[11.5px] font-mono-data text-[color:var(--cc-ink-muted)]">
            {users.filter((u) => showDeactivated || u.active !== false).length} shown
          </div>
        </div>
        <div className="divide-y divide-[#eef1f6]">
          {users
            .filter((u) => showDeactivated || u.active !== false)
            .map((u) => {
            const isSelf = u.id === user?.id;
            const isDeactivated = u.active === false;
            const targetIsPlatformAdmin = u.role === "platform_admin";
            // Owner cannot edit platform_admin users, cannot demote another owner,
            // and cannot touch their own row (self role must not be self-changed).
            const editableByOwner = user?.role === "owner"
              && !targetIsPlatformAdmin
              && !(u.role === "owner" && !isSelf)  // don't let owner-A edit owner-B
              && !isSelf;
            // CC-261: Administrator manages tenant users like an owner but never owner/platform_admin rows.
            const editableByAdministrator = user?.role === "administrative"
              && !targetIsPlatformAdmin && u.role !== "owner" && !isSelf;
            const editable = (user?.role === "platform_admin" && !isSelf) || editableByOwner || editableByAdministrator;
            return (
              <div
                key={u.id}
                data-testid={`user-row-${u.email}`}
                data-deactivated={isDeactivated ? "true" : "false"}
                className={`py-3 flex items-center justify-between gap-3 flex-wrap ${isDeactivated ? "opacity-60" : ""}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-[14px] text-[#0e1420] truncate">{u.name}</div>
                    {isDeactivated && (
                      <span
                        data-testid={`user-deactivated-chip-${u.email}`}
                        className="text-[9.5px] font-mono-data uppercase tracking-widest bg-[#f5f6f8] text-[#5c6877] border border-[#e7e9ee] rounded px-1.5 py-0.5"
                      >
                        Deactivated
                      </span>
                    )}
                    {isSelf && (
                      <span className="text-[9.5px] font-mono-data uppercase tracking-widest bg-[#e7f4fb] text-[#1c6a99] border border-[#c5e0ee] rounded px-1.5 py-0.5">
                        You
                      </span>
                    )}
                  </div>
                  <div className="text-[13px] text-[color:var(--cc-ink-muted)] font-mono-data truncate">{u.email}</div>
                </div>
                <div className="flex items-center gap-2">
                  {editable ? (
                    <select
                      value={u.role}
                      onChange={(e) => setPendingRole({ user: u, newRole: e.target.value })}
                      data-testid={`user-role-select-${u.email}`}
                      disabled={savingUserId === u.id}
                      className="text-[11.5px] font-mono-data uppercase tracking-widest bg-white border border-[#e7e9ee] rounded px-2 py-1"
                    >
                      {["owner", "administrative", "call_center", "technician"].filter((r) => r !== "owner" || user?.role !== "administrative").map((r) => (
                        <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-[11.5px] font-mono-data uppercase tracking-widest bg-[#f5f6f8] border border-[#e7e9ee] rounded px-2 py-1 whitespace-nowrap">
                      {ROLE_LABEL[u.role] || u.role}
                    </div>
                  )}
                  {editable && !isDeactivated && (
                    <button
                      onClick={() => setPendingDeactivate(u)}
                      data-testid={`user-deactivate-${u.email}`}
                      className="text-[11.5px] font-semibold text-[#c73535] hover:bg-[#fdecec] px-2 py-1 rounded border border-[#f4c9c9]"
                    >
                      Deactivate
                    </button>
                  )}
                  {editable && isDeactivated && (
                    <button
                      onClick={() => reactivateUser(u)}
                      data-testid={`user-reactivate-${u.email}`}
                      className="text-[11.5px] font-semibold text-[#0e8a68] hover:bg-[#e6f9f2] px-2 py-1 rounded border border-[#c1eddc]"
                    >
                      Reactivate
                    </button>
                  )}
                  {user?.role === "platform_admin" && !isSelf && (
                    <button
                      onClick={() => openResetModal(u)}
                      data-testid={`user-reset-password-${u.email}`}
                      title="Mint a one-time password reset link (admin-generated, 8h TTL)"
                      disabled={savingUserId === u.id || (resetBusy && pendingReset?.id === u.id)}
                      className="text-[11.5px] font-semibold text-[#1c6a99] hover:bg-[#e7f4fb] px-2 py-1 rounded border border-[#c5e0ee] disabled:opacity-50"
                    >
                      Reset Password
                    </button>
                  )}
                  {user?.role === "platform_admin" && !isSelf && (
                    <button
                      onClick={() => setPendingHardDelete(u)}
                      data-testid={`user-hard-delete-${u.email}`}
                      title="Permanently delete user (platform_admin only)"
                      className="text-[11.5px] font-semibold text-[#7a1616] hover:bg-[#fdecec] px-2 py-1 rounded border border-[#d18b8b]"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {users.length === 0 && <div className="py-5 text-[14px] text-[color:var(--cc-ink-muted)]">No users found.</div>}
        </div>
        <div className="mt-4 text-[12px] text-[color:var(--cc-ink-muted)]">
          Signed in as <span className="font-semibold text-[#0e1420]">{user?.name}</span> · <span className="font-mono-data">{user?.role}</span>
        </div>
      </section>

      {canCreate && (
        <section className="bg-white border border-[#e7e9ee] rounded-xl p-5" data-testid="invites-section">
          <div className="flex items-center justify-between mb-4 gap-3">
            <h3 className="font-display font-semibold text-[15px] text-[#0e1420]">Invite links</h3>
            <div className="text-[12px] text-[color:var(--cc-ink-muted)]">
              {invites.filter((i) => i.status === "pending").length} pending
            </div>
          </div>
          {invites.length === 0 ? (
            <div className="py-5 text-[13.5px] text-[color:var(--cc-ink-muted)]">
              No invites generated yet. Use <span className="font-semibold text-[#0e1420]">Generate invite link</span> to create one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-[13px]">
                <thead className="bg-[#f5f6f8] text-[10.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)] font-bold">
                  <tr>
                    <th className="text-left py-2 px-3">Role</th>
                    <th className="text-left py-2 px-3">Recipient</th>
                    <th className="text-left py-2 px-3">Type</th>
                    <th className="text-left py-2 px-3">Status</th>
                    <th className="text-left py-2 px-3">Expires</th>
                    <th className="text-right py-2 px-3">Uses</th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => (
                    <tr key={inv.id} className="border-t border-[#eef1f6]" data-testid={`invite-row-${inv.id}`}>
                      <td className="py-2.5 px-3 font-semibold text-[#0e1420]">{ROLE_LABEL[inv.role] || inv.role}</td>
                      <td className="py-2.5 px-3 text-[color:var(--cc-ink-muted)] font-mono-data truncate max-w-[220px]">
                        {inv.email || <span className="italic text-[#8a6a1c]">open link</span>}
                      </td>
                      <td className="py-2.5 px-3">{inv.single_use ? "Single-use" : "Reusable"}</td>
                      <td className="py-2.5 px-3">
                        <span className={`text-[10.5px] font-mono-data uppercase tracking-widest border rounded px-2 py-0.5 font-bold ${STATUS_STYLES[inv.status] || STATUS_STYLES.pending}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-[color:var(--cc-ink-muted)]"><Clock size={11} strokeWidth={1.8} className="inline mr-1" />{fmtRel(inv.expires_at)}</td>
                      <td className="py-2.5 px-3 text-right font-mono-data font-semibold">{inv.use_count}</td>
                      <td className="py-2.5 px-3 text-right">
                        {inv.status === "pending" && (
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => regenerate(inv)} data-testid={`invite-regen-${inv.id}`}
                                    title="Revoke and regenerate this invite with a fresh token"
                                    className="p-1.5 rounded-md hover:bg-[#e7f4fb] text-[#1c6a99]">
                              <RefreshCw size={14} strokeWidth={1.8} />
                            </button>
                            <button onClick={() => revoke(inv.id)} data-testid={`invite-revoke-${inv.id}`}
                                    title="Revoke this invite"
                                    className="p-1.5 rounded-md hover:bg-[#fee2e2] text-[#991b1b]">
                              <Trash2 size={14} strokeWidth={1.8} />
                            </button>
                          </div>
                        )}
                        {(inv.status === "consumed" || inv.status === "expired" || inv.status === "revoked") && inv.email && (
                          <button onClick={() => regenerate(inv)} data-testid={`invite-regen-${inv.id}`}
                                  title="Issue a new invite for the same email/role"
                                  className="p-1.5 rounded-md hover:bg-[#e7f4fb] text-[#1c6a99]">
                            <RefreshCw size={14} strokeWidth={1.8} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Personal notifications preferences — per-user watchlist */}
      <NotificationWatchlist />

      {/* Platform-admin-only surgical delete. Sits at the bottom on purpose —
          this is a last-resort action, not part of normal Settings flow. */}
      {user?.role === "platform_admin" && <DangerZone />}

      {creating && <CreateUserModal user={user} tenant={tenant} onClose={() => setCreating(false)} onCreated={loadUsers} />}

      {/* Role-change confirm modal — lightweight, no typed-ID. Both directions
          reversible so a full DangerZone gate isn't warranted. */}
      {pendingRole && (
        <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="role-change-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-5 border border-[#e7e9ee]">
            <h3 className="font-display font-bold text-[16px] text-[#0e1420]">Change {pendingRole.user.name}&rsquo;s role?</h3>
            <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-2 leading-relaxed">
              From <span className="font-mono-data font-semibold text-[#0e1420]">{ROLE_LABEL[pendingRole.user.role] || pendingRole.user.role}</span>
              {" "}to <span className="font-mono-data font-semibold text-[#0072bc]">{ROLE_LABEL[pendingRole.newRole] || pendingRole.newRole}</span>.
              Change is audit-logged and can be reversed here anytime.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setPendingRole(null)} className="cc-btn-ghost text-[13px]" data-testid="role-change-cancel">Cancel</button>
              <button onClick={confirmRoleChange} className="cc-btn-primary text-[13px]" data-testid="role-change-confirm" disabled={savingUserId === pendingRole.user.id}>
                {savingUserId === pendingRole.user.id ? "Saving\u2026" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate confirm modal. Lightweight — same reversibility argument. */}
      {pendingDeactivate && (
        <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="deactivate-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-5 border border-[#e7e9ee]">
            <h3 className="font-display font-bold text-[16px] text-[#0e1420]">Deactivate {pendingDeactivate.name}?</h3>
            <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-2 leading-relaxed">
              <span className="font-mono-data">{pendingDeactivate.email}</span> will be signed out and can&rsquo;t log in until reactivated. All their history (KPI entries, L10 items, audit trail) stays intact and attributed.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setPendingDeactivate(null)} className="cc-btn-ghost text-[13px]" data-testid="deactivate-cancel">Cancel</button>
              <button onClick={confirmDeactivate}
                      className="text-[13px] font-semibold bg-[#c73535] hover:bg-[#a52020] text-white px-4 py-2 rounded-md"
                      data-testid="deactivate-confirm"
                      disabled={savingUserId === pendingDeactivate.id}>
                {savingUserId === pendingDeactivate.id ? "Saving\u2026" : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hard-delete modal — full DangerZone-style typed-email confirmation. */}
      {pendingHardDelete && (
        <div className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="hard-delete-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-5 border-2 border-[#c73535]">
            <h3 className="font-display font-bold text-[16px] text-[#7a1616]">Permanently delete {pendingHardDelete.name}?</h3>
            <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-2 leading-relaxed">
              This <b>cannot be undone</b>. The user record is erased, though any KPI entries / L10 items they created will keep the reference. Consider <b>Deactivate</b> instead unless you specifically need the record gone.
            </p>
            <div className="mt-4">
              <div className="text-[11.5px] uppercase tracking-widest font-semibold text-[color:var(--cc-ink-muted)] mb-1.5">
                Type <span className="font-mono-data text-[#7a1616]">{pendingHardDelete.email}</span> to confirm
              </div>
              <input
                type="text"
                value={hardDeleteConfirm}
                onChange={(e) => setHardDeleteConfirm(e.target.value)}
                autoFocus
                data-testid="hard-delete-confirm-input"
                className="w-full border border-[#e7e9ee] rounded-md px-3 py-2 text-[13.5px] font-mono-data"
                placeholder={pendingHardDelete.email}
              />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => { setPendingHardDelete(null); setHardDeleteConfirm(""); }}
                className="cc-btn-ghost text-[13px]"
                data-testid="hard-delete-cancel"
              >Cancel</button>
              <button
                onClick={confirmHardDelete}
                disabled={hardDeleteConfirm.toLowerCase().trim() !== (pendingHardDelete.email || "").toLowerCase()}
                data-testid="hard-delete-confirm"
                className="text-[13px] font-semibold bg-[#7a1616] hover:bg-[#5a0f0f] disabled:opacity-40 disabled:cursor-not-allowed text-white px-4 py-2 rounded-md"
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
      {inviting && <InviteGeneratorModal user={user} tenant={tenant} onClose={() => setInviting(false)} onCreated={loadInvites} />}

      {/* CC-093 (2026-09-29) — admin password reset modal. Shows the URL
          returned by POST /api/admin/users/{id}/mint-reset-token, auto-
          copies on open (best-effort — silently degrades in non-secure
          contexts), and provides a manual Copy button as the fallback. */}
      {pendingReset && (
        <div
          className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          data-testid="reset-password-modal"
          onClick={closeResetModal}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-[560px] shadow-2xl overflow-hidden border border-[#e7e9ee]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-[#eef1f6] flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-[17px] text-[#0e1420]">
                  Reset password for {pendingReset.name}
                </div>
                <div className="text-[12px] text-[color:var(--cc-ink-muted)] font-mono-data mt-0.5">
                  {pendingReset.email}
                </div>
              </div>
              <button
                onClick={closeResetModal}
                data-testid="reset-password-close"
                className="p-1.5 rounded-md hover:bg-[#f5f6f8]"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {resetBusy && (
                <div
                  className="text-[13px] text-[color:var(--cc-ink-muted)]"
                  data-testid="reset-password-loading"
                >
                  Generating one-time reset link&hellip;
                </div>
              )}
              {resetErr && (
                <div
                  className="text-[12.5px] text-[#7a1c1c] bg-[#fdecec] border border-[#f4c9c9] rounded-md px-3 py-2"
                  data-testid="reset-password-error"
                >
                  {resetErr}
                </div>
              )}
              {resetResult && (
                <>
                  <div className="text-[13px] text-[color:var(--cc-ink-muted)] leading-relaxed">
                    One-time reset link ready. Hand it to <span className="font-semibold text-[#0e1420]">{pendingReset.name}</span> via chat, SMS, or in-person &mdash; anyone with this URL can set a new password. Expires <span className="font-semibold text-[#0e1420]">{fmtRel(resetResult.expires_at)}</span>.
                  </div>
                  <div className="flex items-stretch gap-2">
                    <input
                      readOnly
                      value={resetResult.reset_url}
                      data-testid="reset-password-url-input"
                      onFocus={(e) => e.target.select()}
                      className="flex-1 bg-[#fafbfc] border border-[#e7e9ee] rounded-lg px-3 py-2.5 text-[12.5px] font-mono-data text-[#0e1420] outline-none"
                    />
                    <button
                      onClick={copyResetUrl}
                      data-testid="reset-password-copy"
                      className={`shrink-0 px-3 py-2 rounded-lg text-[12.5px] font-semibold border ${
                        resetCopied
                          ? "bg-[#e6f9f2] text-[#0e6b52] border-[#c1eddc]"
                          : "bg-white text-[#0e1420] border-[#e7e9ee] hover:bg-[#f5f6f8]"
                      }`}
                    >
                      {resetCopied
                        ? (<span className="inline-flex items-center gap-1"><Check size={13} /> Copied</span>)
                        : (<span className="inline-flex items-center gap-1"><Copy size={13} /> Copy</span>)}
                    </button>
                  </div>
                  <div className="text-[11.5px] text-[color:var(--cc-ink-muted)]">
                    This link works once and expires in 8 hours. If it&rsquo;s lost, mint a new one &mdash; the old one is invalidated the moment {pendingReset.name} completes the reset.
                  </div>
                </>
              )}
            </div>
            <div className="p-4 border-t border-[#eef1f6] flex justify-end">
              <button
                onClick={closeResetModal}
                data-testid="reset-password-done"
                className="cc-btn-primary text-[13px]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      {regenerated && (
        <div data-testid="invite-regen-modal" className="fixed inset-0 bg-black/45 backdrop-blur-sm z-50 flex items-center justify-center p-4"
             onClick={() => setRegenerated(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-[540px] shadow-2xl overflow-hidden">
            <div className="p-5 border-b border-[#eef1f6] flex items-center justify-between">
              <div className="font-display font-bold text-[17px] text-[#0e1420]">Fresh invite link ready</div>
              <button onClick={() => setRegenerated(null)} data-testid="invite-regen-close" className="p-1.5 rounded-md hover:bg-[#f5f6f8]"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-[13px] text-[color:var(--cc-ink-muted)]">
                Previous link revoked. Share this new one manually — expires <span className="font-semibold text-[#0e1420]">{fmtRel(regenerated.expires_at)}</span>.
                {regenerated.email ? <> Locked to <span className="font-mono-data font-semibold text-[#0e1420]">{regenerated.email}</span>.</> : <> Reusable by anyone with the link.</>}
              </div>
              <div className="flex items-stretch gap-2">
                <input readOnly value={regenerated.url} data-testid="invite-regen-url"
                       className="flex-1 bg-[#fafbfc] border border-[#e7e9ee] rounded-lg px-3 py-2.5 text-[12.5px] font-mono-data text-[#0e1420] outline-none" />
                <button onClick={copyRegenerated} data-testid="invite-regen-copy"
                        className="cc-btn-primary text-[13px] py-2 px-3 shrink-0">
                  {regenCopied ? <><Check size={14} strokeWidth={2} /> Copied</> : <><Copy size={14} strokeWidth={2} /> Copy</>}
                </button>
              </div>
              <button onClick={() => setRegenerated(null)} data-testid="invite-regen-done" className="cc-btn-secondary w-full text-[13px] py-2">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
